// Pixel Office Types
// Ported from pixel-agents JSON layout system

export const TILE_SIZE = 16;

// Tile types - matching pixel-agents layout format
export const TileType = {
  WALL: 0,
  FLOOR_1: 1,
  FLOOR_2: 2,
  FLOOR_3: 3,
  FLOOR_4: 4,
  FLOOR_5: 5,
  FLOOR_6: 6,
  FLOOR_7: 7,
  VOID: 8,
} as const;
export type TileType = (typeof TileType)[keyof typeof TileType];

/** Per-tile color settings for floor colorization (HSBC values) */
export interface FloorColor {
  /** Hue: 0-360 */
  h: number;
  /** Saturation: 0-100 */
  s: number;
  /** Brightness -100 to 100 */
  b: number;
  /** Contrast -100 to 100 */
  c: number;
  /** If true, use Photoshop-style Colorize mode (grayscale→HSL) */
  colorize?: boolean;
}

/** Furniture instance ready for z-sorted rendering */
export interface FurnitureInstance {
  sprite: SpriteData;
  x: number;
  y: number;
  zY: number;
}

/** Furniture placed in the office */
export interface PlacedFurniture {
  uid: string;
  type: string; // ASSET_* ID
  col: number;
  row: number;
  /** Optional color override */
  color?: FloorColor;
}

/** JSON layout format - matching pixel-agents */
export interface LayoutData {
  version: 1;
  cols: number;
  rows: number;
  tiles: TileType[];
  tileColors?: Array<FloorColor | null>;
  furniture: PlacedFurniture[];
}

export const CharacterState = {
  IDLE: "idle",
  WALK: "walk",
  TYPE: "type",
  SLEEP: "sleep", // in bunk room, resting
  SIT_COUCH: "sit_couch", // sitting on couch
  DRINK: "drink", // at water cooler
  WANDER: "wander", // walking around randomly
  MEETING: "meeting", // in meeting room with other agents
  COLLABORATE: "collaborate", // collaborating/pairing with another agent
} as const;
export type CharacterState = (typeof CharacterState)[keyof typeof CharacterState];

export const Direction = {
  DOWN: 0,
  LEFT: 1,
  RIGHT: 2,
  UP: 3,
} as const;
export type Direction = (typeof Direction)[keyof typeof Direction];

/** 2D array of hex color strings (or '' for transparent). [row][col] */
export type SpriteData = string[][];

export interface Character {
  id: string;
  name: string;
  state: CharacterState;
  dir: Direction;
  x: number;
  y: number;
  tileCol: number;
  tileRow: number;
  path: Array<{ col: number; row: number }>;
  moveProgress: number;
  currentTool: string | null;
  palette: number;
  frame: number;
  frameTimer: number;
  isActive: boolean;
  isSubagent: boolean;
  parentAgentId: string | null;
  /** Active speech bubble type, or null if none showing */
  bubbleType: "permission" | "waiting" | "message" | "warning" | "setup" | "speech" | "discuss" | "sleep" | "success" | null;
  /** Count of items needing attention (shown as badge on warning bubble) */
  attentionCount?: number;
  /** Countdown timer for bubble */
  bubbleTimer: number;
  /** Active matrix spawn/despawn effect, or null */
  matrixEffect: "spawn" | "despawn" | null;
  /** Timer counting up from 0 to MATRIX_EFFECT_DURATION */
  matrixEffectTimer: number;
  /** Per-column random seeds (16 values) for staggered rain timing */
  matrixEffectSeeds: number[];
  /** Whether character is selected */
  selected: boolean;
  /** Whether character is hovered */
  hovered: boolean;
  /** Team role for multi-agent teams */
  role?: AgentRole;
  /** ID of agent this character is collaborating with */
  collaboratingWith?: string | null;
  /** Whether this character represents the user */
  isUserAvatar?: boolean;
  /** Whether this character is a band member (prefers sleep when idle) */
  isBandMember?: boolean;
  /** Timer for discussion animation (alternating bubbles) */
  discussionTimer?: number;
  /** Whether currently showing discussion bubble */
  isDiscussing?: boolean;
  /** IDs of agents this character is working together with on same task */
  workingTogetherWith?: string[];
  /** Focus target - agent this character is observing/directing */
  focusTargetId?: string | null;
  /** Timer for idle behavior duration */
  idleBehaviorTimer?: number;
  /** Target duration for current idle activity (set once, not recalculated) */
  idleDuration?: number;
  /** Target idle point name */
  idleTargetPoint?: string | null;
  /** Home desk coordinates for returning after idle */
  homeDesk?: { col: number; row: number } | null;
  /** Meeting room seat index (0-5) when in meeting */
  meetingSeatIndex?: number | null;
  /** Whether this agent initiated the current meeting */
  isMeetingInitiator?: boolean;
  /** Timer for meeting duration */
  meetingTimer?: number;
  /** IDs of agents in the same meeting */
  meetingParticipants?: string[];
  /** Visual handoff animation progress (0-1) */
  handoffProgress?: number;
  /** ID of agent receiving/giving handoff */
  handoffTargetId?: string | null;
  /** Real-time metrics for overlay display */
  metricsOverlay?: {
    tokensPerSec?: number;
    costCents?: number;
    latencyMs?: number;
    lastUpdated?: number;
  };
  /** Current status/thinking text to display (e.g., "Reading file...", "Searching...") */
  statusText?: string | null;
}

export interface Desk {
  id: string;
  col: number;
  row: number;
  assignedAgentId: string | null;
  facingDir: Direction;
}

export interface OfficeLayout {
  cols: number;
  rows: number;
  desks: Desk[];
  // Extended with JSON layout data
  layout?: LayoutData;
}

export interface AgentActivity {
  agentId: string;
  type: "idle" | "thinking" | "typing" | "reading" | "calling" | "coding";
  tool?: string;
  status?: string;
}

export interface SubagentSpawn {
  parentAgentId: string;
  toolId: string;
  name?: string;
}

/** Team agent role for multi-agent teams */
export type AgentRole =
  | "coordinator"
  | "engineer"
  | "pm"
  | "devops"
  | "analyst"
  | "writer"
  | "support"
  | "custom";

/** Visual appearance customization */
export interface AgentAppearance {
  hairColor: string;
  hairStyle: "short" | "long" | "bald" | "ponytail";
  outfit: "suit" | "hoodie" | "casual" | "uniform";
  skinTone: number; // palette index
}

/** Team member definition */
export interface TeamMember {
  id: string;
  name: string;
  role: AgentRole;
  appearance: AgentAppearance;
  skills: string[];
  isUser?: boolean; // true if this represents the user's avatar
}

/** Persona status */
export type PersonaStatus = "active" | "resting" | "archived";

/** Persona definition - portable, shareable agent configuration */
export interface Persona {
  id: string;
  name: string;
  role: AgentRole;
  appearance: AgentAppearance;

  personality: {
    style: string; // "Direct, technical, ships fast"
    voice: string; // "Terse responses, uses code examples"
  };

  skills: {
    enabled: string[];
    disabled: string[];
  };

  preferences: {
    model?: string; // preferred model for this persona
    autonomy: "low" | "medium" | "high";
  };

  systemPromptAdditions?: string; // extra instructions for this persona

  status: PersonaStatus;
  created: string;
  lastActive: string;

  // Marketplace fields (optional)
  author?: string;
  version?: string;
  description?: string;
  downloads?: number;
  rating?: number;
}

/** Inter-agent communication event */
export interface AgentMessage {
  fromAgentId: string;
  toAgentId: string;
  type: "task" | "question" | "response" | "collaboration";
  content: string;
  timestamp: number;
}

/** Integration type for interactive office objects */
export type IntegrationType = "phone" | "computer" | "notepad" | "calendar" | "email";

/** Interactive office object that can be clicked to trigger setup */
export interface InteractiveObject {
  id: string;
  type: IntegrationType;
  /** Grid column position */
  col: number;
  /** Grid row position */
  row: number;
  /** Width in tiles */
  width: number;
  /** Height in tiles */
  height: number;
  /** Whether this integration is configured */
  configured: boolean;
  /** Tooltip text shown on hover */
  tooltip: string;
  /** Direction the object faces (for rendering) */
  facing?: Direction;
}

/** Collaboration state - when agents are working together */
export interface Collaboration {
  agents: string[];
  type: "discussion" | "review" | "handoff" | "pairing";
  topic?: string;
}

/** Tools that show reading animation instead of typing */
export const READING_TOOLS = new Set(["Read", "Grep", "Glob", "WebFetch", "WebSearch"]);

export function isReadingTool(tool: string | null): boolean {
  if (!tool) return false;
  return READING_TOOLS.has(tool);
}

/** Generate random seeds for matrix effect */
export function generateMatrixSeeds(): number[] {
  const seeds: number[] = [];
  for (let i = 0; i < 16; i++) {
    seeds.push(Math.random());
  }
  return seeds;
}
